import { trackAnalyticsEvent } from '@autone/analytics';
import { useAutoneTranslation } from '@autone/translations';
import {
  capitalizeFirstLetter,
  fISOToReadableNoTimeShort,
} from '@autone/utils';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import {
  Card,
  ClickAwayListener,
  Divider,
  IconButton,
  Popper,
  Skeleton,
  Stack,
  type SxProps,
  useTheme,
} from '@mui/material';
import React, { useRef } from 'react';

import { type FullScreenContainer } from '../../../components/table/types';

import usePeakViewMetricTitles from './hooks/usePeakViewMetricTitles';
import { MetricGroup, MetricHeaderAndValue } from './PeakViewMetrics';
import PriceTypeList from './PriceTypeList';
import { getConcatenatedSeasonEvent } from './utils';

const CARD_WIDTH = '283px';

const PeakViewCardSkeleton = ({ cardSxProps }: { cardSxProps: SxProps }) => {
  const skeletonGroup = (
    <Stack direction={'row'} gap={1}>
      <Skeleton sx={{ transform: 'none' }} width={'100%'} height={'38px'} />
      <Skeleton sx={{ transform: 'none' }} width={'100%'} height={'38px'} />
    </Stack>
  );
  return (
    <Card sx={{ ...cardSxProps, height: '318px' }}>
      <Stack p={1} spacing={2.5}>
        <Skeleton sx={{ transform: 'none' }} width={'100%'} height={'58px'} />
        {skeletonGroup}
        {skeletonGroup}
        {skeletonGroup}
        <Stack sx={{ pt: 1 }} alignItems={'flex-end'}>
          <Skeleton sx={{ transform: 'none' }} width={'50%'} height={'35px'} />
        </Stack>
      </Stack>
    </Card>
  );
};

const PeakViewCard = ({
  isLoading,
  priceTypes,
  defaultCurrency,
  season,
  event,
  firstSale,
  lifeToDateSales,
  department,
  material,
  subDepartment,
  gender,
}: {
  isLoading: boolean;
  priceTypes?: { value: number; label: string }[];
  defaultCurrency?: string;
  season?: string | null;
  event?: string | null;
  lifeToDateSales?: string;
  firstSale?: string | null;
  department?: string | null;
  material?: string | null;
  subDepartment?: string | null;
  gender?: string | null;
}) => {
  const { common } = useAutoneTranslation();
  const theme = useTheme();
  const cardSxProps = {
    borderRadius: 1,
    border: '1px solid grey.100',
    width: CARD_WIDTH,
  };
  const metricTitles = usePeakViewMetricTitles();

  if (isLoading) {
    return <PeakViewCardSkeleton cardSxProps={cardSxProps} />;
  }

  return (
    <Card sx={cardSxProps}>
      <Stack p={2} spacing={2}>
        <PriceTypeList
          priceTypes={priceTypes}
          defaultCurrency={defaultCurrency}
        />
        <MetricHeaderAndValue
          metric={{
            title: metricTitles.seasonAndEvent,
            value: getConcatenatedSeasonEvent(season, event),
          }}
          width="100%"
          missingDataText={common('peak-view.data-missing')}
          metricSx={{
            fontSize: theme.typography.h6.fontSize,
          }}
        />
        <MetricGroup
          missingDataText={common('peak-view.data-missing')}
          metrics={[
            {
              title: metricTitles.firstSale,
              value: firstSale,
              formatter: fISOToReadableNoTimeShort,
            },
            {
              title: metricTitles.lifeToDateSales,
              value: lifeToDateSales,
            },
            {
              title: metricTitles.department,
              value: department,
              formatter: capitalizeFirstLetter,
            },
            {
              title: metricTitles.subDepartment,
              value: subDepartment,
              formatter: capitalizeFirstLetter,
            },
            {
              title: metricTitles.material,
              value: material,
              formatter: capitalizeFirstLetter,
            },
            {
              title: metricTitles.gender,
              value: gender,
              formatter: capitalizeFirstLetter,
            },
          ]}
        />
      </Stack>
      <Divider sx={{ width: '100%' }} />
    </Card>
  );
};

const PeakViewTooltip = ({
  isHovered,
  isOpen,
  setIsOpen,
  priceTypes,
  defaultCurrency,
  isLoading,
  season,
  event,
  firstSale,
  department,
  material,
  subDepartment,
  gender,
  isFullScreen,
  fullScreenContainer,
  fullScreenContainerElement,
  lifeToDateSales,
}: {
  isHovered: boolean;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  priceTypes?: { value: number; label: string }[];
  defaultCurrency?: string;
  isLoading: boolean;
  season?: string | null;
  event?: string | null;
  firstSale?: string | null;
  lifeToDateSales?: string;
  department?: string | null;
  material?: string | null;
  subDepartment?: string | null;
  gender?: string | null;
  isFullScreen?: boolean;
  /**
   * @deprecated Use fullScreenContainerElement instead
   */
  fullScreenContainer?: FullScreenContainer;
  fullScreenContainerElement?: HTMLDivElement | null;
}) => {
  const { common } = useAutoneTranslation();
  const ref = useRef<HTMLButtonElement>(null);

  const handlePeakViewIconClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    trackAnalyticsEvent({
      category: 'common',
      action: 'peak_view_button_clicked',
    });
    setIsOpen(!isOpen);
  };

  if (!isHovered && !isOpen) return null;

  const props = {
    isLoading,
    priceTypes,
    defaultCurrency,
    season,
    event,
    firstSale,
    department,
    material,
    subDepartment,
    gender,
    lifeToDateSales,
  };
  return (
    <>
      <IconButton
        onClick={handlePeakViewIconClick}
        ref={ref}
        title={common('peak-view.tooltip.button-aria-title')}
        sx={{
          p: 0,
          height: '14px',
          width: '14px',
          '&:hover': {
            backgroundColor: 'primary.lighter',
            borderRadius: '2px',
          },
        }}
      >
        <ArrowDropDownRoundedIcon sx={{ color: 'primary.main' }} />
      </IconButton>
      <Popper
        sx={{
          zIndex: 9999,
          '&[data-popper-reference-hidden]': {
            visibility: 'hidden',
            pointerEvents: 'none',
          },
        }}
        open={isOpen}
        anchorEl={ref.current}
        placement="bottom"
        container={
          isFullScreen
            ? (fullScreenContainerElement ?? fullScreenContainer?.current)
            : undefined
        }
      >
        <ClickAwayListener
          mouseEvent="onMouseDown"
          onClickAway={() => {
            setIsOpen(false);
          }}
        >
          <div>
            <PeakViewCard {...props} />
          </div>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default PeakViewTooltip;
